import React from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormField from "../utils/FormFileds";
import ButtonComponent from "../Ui/Button";
import useApiHandler from "../hooks/ApiHandler";
import { Toaster, toast } from "react-hot-toast";
import { WarrantyRequests } from "../../service";

const WarrantySchema = Yup.object().shape({
  details: Yup.string().trim().required("Please provide details"),
  media: Yup.mixed().nullable(),
});

const FieldRow = ({ label, value }) => (
  <div className="flex flex-col gap-1 w-full">
    <label className="text-[12px] text-[#595959]">{label}</label>
    <input
      value={value || ""}
      readOnly
      className="w-full bg-[#F8F8F8] rounded-[8px] px-3 py-2 text-sm text-[#242424]"
    />
  </div>
);

const WarrantyServicePage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { callApi, loading } = useApiHandler();
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const invoice = state?.invoice || {};
  const job = invoice?.jobSnapshot || {};

  const initialValues = {
    details: "",
    media: null,
  };

  const jobNumberRaw = job?.jobNumber || invoice?.jobNumber || params?.jobId;
  // Pad to 9 digits only when it's numeric-ish; otherwise send as string
  const jobNumber = (() => {
    const digits = String(jobNumberRaw || "").replace(/\D/g, "");
    if (digits) return digits.padStart(9, "0");
    return String(jobNumberRaw || "");
  })();

  // Keep the raw value (as provided) and create a human-friendly display value.
  const serviceCategoryRaw = (job?.serviceCategory || job?.issueType || job?.jobTitle || "").toString();

  // Humanize helper: convert PEST_CONTROL or pestControl to "Pest Control"
  const humanize = (input) => {
    if (!input && input !== 0) return "";
    let str = String(input);
    // If it's JSON array-like, try to extract first element
    try {
      const maybe = JSON.parse(str);
      if (Array.isArray(maybe) && maybe.length > 0) {
        str = String(maybe[0]);
      }
    } catch (e) {
      // ignore
    }
    // Replace underscores/hyphens with space
    str = str.replace(/[_-]+/g, " ").trim();
    // Add spaces for camelCase
    str = str.replace(/([a-z])([A-Z])/g, "$1 $2");
    // If it was all uppercase like "PEST CONTROL", lowercase it before capitalizing
    if (str === str.toUpperCase()) str = str.toLowerCase();
    // Normalize multiple spaces
    str = str.replace(/\s+/g, " ").trim();
    // Title Case
    return str
      .split(" ")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  };

  const serviceCategoryDisplay = humanize(serviceCategoryRaw);
  const serviceCategoryForPayload = serviceCategoryRaw ? serviceCategoryRaw.toString().toUpperCase() : "";

  const property = (() => {
    const addr = job?.propertyAddress || job?.property_address;
    if (!addr) return "";
    if (typeof addr === "string") return addr;
    if (typeof addr === "object") {
      const parts = [
        addr.streetAddress || addr.street_address,
        (addr.unitApt || addr.unit_apt)
          ? `Apt ${addr.unitApt || addr.unit_apt}`
          : null,
        [addr.city, addr.state].filter(Boolean).join(" "),
        addr.zipCode || addr.zip_code,
        addr.country,
      ].filter(Boolean);
      return parts.join(", ");
    }
    return String(addr);
  })();

  const handleSubmit = async (values) => {
    const payload = {
      jobNumber,
      // send the original style (uppercase) to the API to keep backward compatibility
      serviceCategory: serviceCategoryForPayload,
      property,
      details: values.details,
      media: values.media,
    };

    await callApi(
      () => WarrantyRequests.create(payload),
      {
        onSuccess: () => {
          setIsSubmitted(true);
        },
      }
    );
  };

  return (
    <div className="pt-[103px] max-w-[1440px] m-auto md:px-6 px-4">
      <Toaster />
      {/* Header: centered title with right-aligned button (button layout unaffected) */}
      <div className="relative mb-6 py-2">
        {/* absolutely centered title block (won't affect document flow) */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="pointer-events-auto flex flex-col items-center text-center">
            <h1 className="text-[24px] md:text-[32px] font-medium text-[#242424]">
              {isSubmitted ? "Success!" : "Uh Oh!"}
            </h1>
            <p className="text-[#595959] text-[16px] mt-1">
              {isSubmitted 
                ? "Your warranty request has been submitted successfully." 
                : "Looks like something's not quite right. Let's get it fixed."}
            </p>
          </div>
        </div>

        {/* Right-aligned button stays in normal flow and above the centered block when needed */}
        <div className="flex justify-end items-center relative z-10">
          <ButtonComponent bgColor="#3CAB5C" color="#FFFFFF" onClick={() => navigate("/dashboard")}>Back to Dashboard</ButtonComponent>
        </div>
      </div>

      <div className="max-w-[720px] w-full m-auto border border-[#ECECEC] rounded-[16px] md:p-8 p-4">
        {!isSubmitted ? (
          <>
            <h2 className="text-center text-[20px] md:text-[24px] font-medium text-[#242424]">Tell Us More</h2>
            <p className="text-center text-[#595959] text-[12px] mt-1">
              Enter the details to submit your warranty request and we will get it sorted out.
            </p>

            <Formik
              initialValues={initialValues}
              validationSchema={WarrantySchema}
              onSubmit={handleSubmit}
            >
              {({ handleSubmit }) => (
                <Form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
                  <div className="grid gap-4 md:grid-cols-2 grid-cols-1">
                    <FieldRow label="Job Number:" value={jobNumber || "Autofill"} />
                    <FieldRow label="Service Category" value={serviceCategoryDisplay || "Autofill"} />
                  </div>
                  <FieldRow label="Property" value={property || "Autofill"} />

                  <FormField
                    type="textarea"
                    name="details"
                    label="Tell us more:"
                    placeholder="Enter here..."
                    rows={5}
                  />

                  <div>
                    <p className="text-[12px] text-[#242424] mb-2">Add a Photo or Video</p>
                    <FormField type="file" name="media" />
                  </div>

                  <div className="flex justify-center mt-4">
                    <ButtonComponent type="submit" bgColor="#3CAB5C" color="#FFFFFF" disabled={loading}>
                      Submit
                    </ButtonComponent>
                  </div>
                </Form>
              )}
            </Formik>
          </>
        ) : (
          <div className="flex flex-col items-center text-center py-6">
            <h2 className="text-[20px] md:text-[24px] font-medium text-[#242424]">Thank You!</h2>
            <p className="text-[#595959] text-[14px] mt-2 mb-8">
              We have received your warranty request and will get back to you shortly.
            </p>
            <ButtonComponent bgColor="#3CAB5C" color="#FFFFFF" onClick={() => navigate("/dashboard")}>
              Back to Dashboard
            </ButtonComponent>
          </div>
        )}
      </div>
    </div>
  );
};

export default WarrantyServicePage;
